import React, { useEffect, useState } from "react";
import { FaEye, FaEdit, FaEnvelope, FaSearch, FaPlus, FaDownload, FaFilePdf, FaArrowDown, FaArrowUp } from "react-icons/fa";
import { RiFileExcel2Fill } from "react-icons/ri";
import { AiFillLike, AiFillDislike } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import CustomerDetailsModal from "./customerDetails";
import jsPDF from "jspdf";
import "jspdf-autotable"; // Import autotable plugin for table support in PDF
import * as XLSX from "xlsx"; // Import XLSX for Excel generation
import { LuArrowDownUp } from "react-icons/lu";


const Dashboard = ({ setCustID }) => {
    const [customers, setCustomers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("A"); // Default to Active
    const [productFilter, setProductFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10); // Default items per page

    const [isModalOpen, setModalOpen] = React.useState(false);
    const [customerID, setCustomerID] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('asc'); // Default to ascending order

    const navigate = useNavigate();


    const fetchCustomers = async () => {
        try {
            let queryParams = [];

            if (statusFilter) {
                queryParams.push(`status=${statusFilter}`);
            }
            if (productFilter) {
                queryParams.push(`productname=${encodeURIComponent(productFilter)}`);
            }

            const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Customer/GetCustomerDetails${queryString}`);
            if (!response.ok) {
                console.error("API request failed.");
                return;
            }
            const data = await response.json();
            // console.log("Received customers data:", data);
            setCustomers(data.data);
        } catch (error) {
            console.error("Error fetching customer data:", error);
        }
    };

    useEffect(() => {
        fetchCustomers();
        setCurrentPage(1); // Reset to page 1 when filter changes
    }, [statusFilter, productFilter]); // Re-fetch when statusFilter or productFilter changes


    // const filteredCustomers = customers.filter(customer =>
    //     Object.values(customer).some(value =>
    //         value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    //     )
    // );

    const filteredCustomers = customers.filter(customer =>
        Object.values(customer).some(value =>
            value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
    );
    
    // Sort customers based on Customer Name and sortOrder
    const sortedCustomers = [...filteredCustomers].sort((a, b) => {
        const nameA = a.mcrCustomerName.toLowerCase();
        const nameB = b.mcrCustomerName.toLowerCase();
        if (sortOrder === 'asc') {
            return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
        } else {
            return nameA > nameB ? -1 : nameA < nameB ? 1 : 0;
        }
    });

    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

    const displayedCustomers = itemsPerPage === "All"
        ? sortedCustomers
        : sortedCustomers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);


    const toggleActive = async (id) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/Customer/UpdateStatusByCustomerId?CustomerId=${id}`,
                {
                    method: 'PATCH', // Corrected method name
                    headers: {
                        'Content-Type': 'application/json', // Ensures correct content type
                    },
                }
            );

            if (!response.ok) {
                console.error('Something went wrong!');
                return;
            }

            // Parse as text instead of JSON
            const data = await response.text();
            // console.log('toggleActive Response:', data);

            fetchCustomers(); // Refresh data after update
        } catch (error) {
            console.error('Error in toggleActive:', error);
        }
    };


    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };


    // Function to generate and download PDF
    const downloadPDF = () => {
        const doc = new jsPDF();

        // Add title to the PDF
        doc.setFontSize(18);
        doc.text('Customer Dashboard', 14, 20);

        // Prepare table data for PDF
        const tableColumn = ["Sl no.", "Customer Name", "Product Name", "Customer Mobnumber", "Customer Email", "From Date", "To Date", "Status"];
        const tableRows = customers.map((customer, index) => [
            (currentPage - 1) * itemsPerPage + index + 1,
            customer.mcrCustomerName,
            customer.mpProductName,
            customer.mcrCustomerTelephoneNo,
            customer.mcrCustomerEmail,
            customer.mcrFromDate,
            customer.mcrToDate,
            customer.mcrStatus === 'A' ? 'Active' : 'Deactive',
        ]);

        // Generate table in PDF with borders
        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 30, // Start the table after the title
            theme: 'grid', // Grid theme already includes borders
            styles: {
                cellPadding: 5,
                fontSize: 10,
                halign: 'center', // Center align text in cells
                valign: 'middle', // Vertically align text in cells
            },
            didDrawCell: (data) => {
                // Draw a border around each cell manually
                const { x, y, width, height } = data.cell;
                doc.setDrawColor(0, 0, 0); // Black color for the border
                doc.setLineWidth(0.5); // Line width for the border
                doc.rect(x, y, width, height); // Draw the border around the cell
            }
        });

        // Download the PDF
        doc.save('Customer_Dashboard.pdf');
        setIsDropdownOpen(!isDropdownOpen);
    };


    const downloadExcel = () => {
        const tableData = customers.map((customer, index) => ({
            "Sl no.": (currentPage - 1) * itemsPerPage + index + 1,
            "Customer Name": customer.mcrCustomerName,
            "Product Name": customer.mpProductName,
            "Customer Number": customer.mcrCustomerTelephoneNo,
            "Customer Email": customer.mcrCustomerEmail,
            "From Date": customer.mcrFromDate,
            "To Date": customer.mcrToDate,
            "Status": customer.mcrStatus === 'A' ? 'Active' : 'Deactive',
        }));

        // Create a new sheet with the title row and the data rows
        const wsWithTitle = XLSX.utils.aoa_to_sheet([
            ["Customer Details"], // Title row
            [], // Empty row for spacing
            ["Sl no.", "Customer Name", "Product Name", "Customer Number", "Customer Email", "From Date", "To Date", "Status"], // Column headers
            ...tableData.map(row => [
                row["Sl no."],
                row["Customer Name"],
                row["Product Name"],
                row["Customer Number"],
                row["Customer Email"],
                row["From Date"],
                row["To Date"],
                row["Status"]
            ])
        ]);

        // Merge cells for the title
        wsWithTitle["!merges"] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } } // Merge cells in the first row for title
        ];

        // Apply styling to the title
        for (let col = 0; col <= 6; col++) {
            const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
            if (!wsWithTitle[cellRef]) continue;
            wsWithTitle[cellRef].s = {
                alignment: { horizontal: "center", vertical: "center" },
                font: { bold: true, sz: 14 }
            };
        }

        // Apply border to the rest of the table
        const range = XLSX.utils.decode_range(wsWithTitle['!ref']); // Get range of the data
        const border = {
            top: { style: 'thin', color: { rgb: "000000" } },
            right: { style: 'thin', color: { rgb: "000000" } },
            bottom: { style: 'thin', color: { rgb: "000000" } },
            left: { style: 'thin', color: { rgb: "000000" } },
        };

        // Loop through each cell and apply the border
        for (let row = range.s.r; row <= range.e.r; row++) {
            for (let col = range.s.c; col <= range.e.c; col++) {
                const cellAddress = { r: row, c: col };
                const cellRef = XLSX.utils.encode_cell(cellAddress);
                if (!wsWithTitle[cellRef]) continue; // Skip empty cells
                wsWithTitle[cellRef].s = { border }; // Apply border
            }
        }

        // Create workbook and append the sheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsWithTitle, "Customer Dashboard");

        // Save the Excel file
        XLSX.writeFile(wb, "Customer_Dashboard.xlsx");
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleEdit = (id) => {
        if (id) {
            setCustID(id);
            navigate('/editcustomer')
        }
    };


    const handleSort = () => {
        setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
    };


    return (
        <>
            <div className="p-4 w-full">
                <div className="bg-white shadow-md rounded-md mx-10">
                    <div className="flex justify-center gap-10 mb-5 pt-2">
                        <div>
                            <h2 className="text-xl font-bold text-center border-b-2 border-[#95B589] pb-1 bg-gradient-to-br from-teal-400 via-blue-400 to-lime-500 text-transparent bg-clip-text">
                                Customer Dashboard
                            </h2>
                        </div>
                    </div>
                    <div className="overflow-x-auto mx-24 pb-10">
                        <div className="text-xs flex items-center gap-1 mb-4 p-1 bg-[#1a1aff] rounded border border-black">
                            <div className="">
                                <select
                                    id="itemsPerPage"
                                    className="border border-gray-400 rounded outline-blue-500 p-2"
                                    value={itemsPerPage === "All" ? "All" : itemsPerPage} // Show "All" if selected
                                    onChange={(e) => {
                                        const value = e.target.value === "All" ? "All" : Number(e.target.value);
                                        setItemsPerPage(value); // Update itemsPerPage to the selected value
                                        setCurrentPage(1); // Reset to page 1 when the number of items per page changes
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={500}>500</option>
                                    <option value="All">ALL</option>
                                </select>
                            </div>
                            <div>
                                <select
                                    className="border border-gray-400 rounded outline-blue-500 p-2 block w-full rounded-xs"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">ALL</option>
                                    <option value="A">Active</option>
                                    <option value="D">Deactive</option>
                                </select>
                            </div>
                            <div>
                                <select
                                    className="border border-gray-400 rounded outline-blue-500 p-2 block w-full rounded-xs"
                                    value={productFilter}
                                    onChange={(e) => setProductFilter(e.target.value)}
                                >
                                    <option value="">---Product Name---</option>
                                    <option value="TRACe PA">TRACe PA</option>
                                    <option value="CAFe">CAFe</option>
                                    <option value="Doc Assist">Doc Assist</option>
                                    <option value="TRACe ENT">TRACe ENT</option>
                                    <option value="EDICT">EDICT</option>
                                </select>
                            </div>
                            <div className="relative w-[33.7%]">
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    className="border border-blue-500 outline-blue-500 p-2 pl-8 w-full rounded"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <FaSearch className="absolute left-2 top-3 text-gray-500" />
                            </div>
                            <div className="text-white text-sm font-bold">Total Customers: {filteredCustomers.length + 1}</div>
                            <div>
                                {/* Dropdown for PDF and Excel download */}
                                <button
                                    type="button"
                                    className="inline-flex justify-center w-full rounded shadow-sm px-4 py-2 bg-green-500 hover:bg-green-600 text-white"
                                    onClick={toggleDropdown} // This toggles the dropdown visibility
                                >
                                    <FaDownload className="mr-2" /> Download
                                </button>
                                {isDropdownOpen && (
                                    <div
                                        className="mt-2 w-24 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
                                        role="menu"
                                        aria-orientation="vertical"
                                        aria-labelledby="options-menu"
                                    >
                                        <div className="py-1" role="none">
                                            <button
                                                onClick={downloadPDF}
                                                className="text-gray-700 block px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-200 rounded"
                                            >
                                                <FaFilePdf className="text-red-600" />
                                                PDF
                                            </button>
                                            <button
                                                onClick={downloadExcel}
                                                className="text-gray-700 block px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-200 rounded"
                                            >
                                                <RiFileExcel2Fill className="text-green-600" />
                                                Excel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <Link to='/register'
                                className="flex items-center gap-2 bg-green-500 text-white px-8 py-2 rounded">
                                <FaPlus /> Add New
                            </Link>
                        </div>
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-[#1a1aff] text-white">
                                    <th className="p-2 border whitespace-nowrap">Sl no.</th>
                                    <th className="p-2 border whitespace-nowrap">
                                        Customer Name
                                        <button onClick={handleSort} className="ml-2">
                                            {<LuArrowDownUp />}
                                        </button>
                                    </th>
                                    <th className="p-2 border whitespace-nowrap">Product Name</th>
                                    <th className="p-2 border whitespace-nowrap">Customer Number</th>
                                    <th className="p-2 border">Customer Email</th>
                                    <th className="p-2 border whitespace-nowrap">Customer Code</th>
                                    <th className="p-2 border whitespace-nowrap">From Date</th>
                                    <th className="p-2 border whitespace-nowrap">To Date</th>
                                    <th className="p-2 border">Status</th>
                                    <th className="p-2 border" colSpan={4}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedCustomers.map((customer, index) => (
                                    <tr key={customer.mcrId} className={`text-center ${index % 2 === 0 ? 'bg-gray-200' : 'bg-white'}`}>
                                        <td className="p-2 border"> {itemsPerPage === "All"
                                            ? index + 1
                                            : (currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td className="p-2 border">{customer.mcrCustomerName}</td>
                                        <td className="p-2 border">{customer.mpProductName}</td>
                                        <td className="p-2 border">{customer.mcrCustomerTelephoneNo}</td>
                                        <td className="p-2 border">{customer.mcrCustomerEmail}</td>
                                        <td className="p-2 border">{customer.mcrCustomerCode}</td>
                                        <td className="p-2 border whitespace-nowrap">{customer.mcrFromDate}</td>
                                        <td className="p-2 border whitespace-nowrap">{customer.mcrToDate}</td>
                                        <td className="p-2 border">{customer.mcrStatus === 'A' ? 'Active' : 'Deactive'}</td>
                                        <td className="p-2 border flex justify-center gap-2">
                                            <button className="p-2 bg-blue-500 text-white rounded" title="View history"
                                                onClick={() => {
                                                    setModalOpen(true);
                                                    setCustomerID(customer.mcrId);
                                                }}>
                                                <FaEye />
                                            </button>
                                            {
                                                customer.mcrStatus === 'A' ? (
                                                    <button
                                                        onClick={() => toggleActive(customer.mcrId)}
                                                        className="p-2 bg-red-500 text-white rounded" title="Click to Deactive">
                                                        <AiFillDislike />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => toggleActive(customer.mcrId)}
                                                        className="p-2 bg-green-500 text-white rounded" title="Click to Active">
                                                        <AiFillLike />
                                                    </button>
                                                )
                                            }
                                            <button onClick={() => handleEdit(customer.mcrId)}
                                                className="p-2 bg-yellow-500 text-white rounded" title="Edit">
                                                <FaEdit />
                                            </button>
                                            <button className="p-2 bg-purple-500 text-white rounded" title="Send mail">
                                                <FaEnvelope />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="flex justify-between mt-4">
                            <button
                                className="px-4 py-2 bg-[#1a1aff] text-white rounded disabled:opacity-50"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(currentPage - 1)}
                            >
                                Previous
                            </button>
                            <span>Page {currentPage} of {totalPages}</span>
                            <button
                                className="px-4 py-2 bg-[#1a1aff] text-white rounded disabled:opacity-50"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(currentPage + 1)}
                            >
                                Next
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            <CustomerDetailsModal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                customerID={customerID}
            />
        </>
    );
};

export default Dashboard;
